import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  InputAdornment,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Container,
  CircularProgress,
  Alert,
  Snackbar,
  Menu,
  Badge,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  LocationCity as LocationCityIcon,
  LocalOffer as LocalOfferIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/AuthProvider';
import dayjs from 'dayjs';
import { formatInTenantTz, formatWithTzLabel } from '../../utils/dateUtils';
import { QUIZ_DASHBOARD_ENDPOINT, CUSTOMERS_QUIZ_ENDPOINT, QUIZ_ATTEMPTS_ENDPOINT, QUIZ_EXPORT_SUMMARY_ENDPOINT, QUIZ_EXPORT_ALL_ENDPOINT } from '../../config/urls';
import AppTable from '../../common/app-table';
import TableSearchInput from '../TableSearchInput';
import AppDropdown from '../../common/app-dropdown';
import ButtonOne from '../../common/normal-button';
import ButtonTwo from '../../common/dashboard-button';
import typography from '../../cssUtils/typography';
import BackButton from '../../common/back-button';

// Column-truncation width for "Audience Name" — see the render comment on
// the audience_name column below for the character/font-size math.
const AUDIENCE_NAME_MAX_WIDTH = 180; // px

// ── Standardized analytics-card style tokens ────────────────────────────────
// Pulled in line with the rest of the dashboard (see typography.js usage in
// the Details view below) instead of one-off gradients per card, so this
// summary screen reads as part of the same design system as the rest of the
// app: white surface, a single accent color per metric, consistent spacing.
const STAT_CARDS_CONFIG = [
  {
    key: 'total_customers',
    label: 'Total Customers',
    icon: PeopleIcon,
    accent: '#1976d2',
    format: (v) => v ?? 0,
  },
  {
    key: 'total_attempts',
    label: 'Total Attempts',
    icon: TrendingUpIcon,
    accent: '#0288d1',
    format: (v) => v ?? 0,
  },
  {
    key: 'average_score',
    label: 'Average Score',
    icon: TrophyIcon,
    accent: '#2e7d32',
    format: (v) => `${(v ?? 0).toFixed ? v.toFixed(1) : v}%`,
  },
  {
    key: 'pass_rate',
    label: 'Pass Rate',
    icon: TrophyIcon,
    accent: '#ed6c02',
    format: (v) => `${(v ?? 0).toFixed ? v.toFixed(1) : v}%`,
  },
];

const StatCard = ({ label, value, Icon, accent, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderLeft: `4px solid ${accent}`,
      borderRadius: 2,
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${accent}1A`, // ~10% tint of accent
        color: accent,
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 26 }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...typography.h2 }} color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography sx={{ ...typography.statLarge }} fontWeight={700} color="text.primary">
        {loading ? '…' : value}
      </Typography>
    </Box>
  </Paper>
);

const QuizDashboardDynamic = () => {
  const { getValidToken } = useAuth();
  const [allResults, setAllResults] = useState([]);
  const [customerSummary, setCustomerSummary] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScore, setFilterScore] = useState('all');
  const [currentView, setCurrentView] = useState('summary');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expandedAttempts, setExpandedAttempts] = useState(new Set());

  // ── Advanced filters ───────────────────────────────────────────────────────
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | passed | failed
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterTags, setFilterTags] = useState('');

  const activeAdvancedFilterCount = useMemo(() => {
    return [filterCity, filterDateFrom, filterDateTo, filterTags]
      .filter((v) => v && v.trim() !== '').length + (filterStatus !== 'all' ? 1 : 0);
  }, [filterCity, filterStatus, filterDateFrom, filterDateTo, filterTags]);

  const clearAdvancedFilters = () => {
    setFilterCity('');
    setFilterStatus('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterTags('');
  };

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Pagination and server-side control
  const [rowCount, setRowCount] = useState(0);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
    serverSidePage: 0,
  });

  // Export dropdown menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  // Dashboard statistics
  const [stats, setStats] = useState({
    total_customers: 0,
    total_attempts: 0,
    average_score: 0,
    pass_rate: 0,
  });

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const token = await getValidToken();
      const tenant = localStorage.getItem('tenant');

      const headers = {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenant || 'default',
        'Authorization': `Bearer ${token}`,
      };

      const response = await fetch(QUIZ_DASHBOARD_ENDPOINT, { headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard statistics');
      showSnackbar('Error loading dashboard statistics', 'error');
    } finally {
      setLoadingStats(false);
    }
  };

  // Fetch customers with server-side filtering and pagination
  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const token = await getValidToken();
      const tenant = localStorage.getItem('tenant');

      // Build query parameters
      const queryParams = new URLSearchParams({
        limit: controller.rowsPerPage.toString(),
        offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      });

      // Add search if present
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }

      // Add score filter if not 'all'
      if (filterScore !== 'all') {
        queryParams.append('score', filterScore);
      }

      // Advanced filters
      if (filterCity) {
        queryParams.append('city', filterCity);
      }
      if (filterStatus !== 'all') {
        queryParams.append('status', filterStatus); // passed | failed
      }
      if (filterDateFrom) {
        queryParams.append('date_from', filterDateFrom);
      }
      if (filterDateTo) {
        queryParams.append('date_to', filterDateTo);
      }
      if (filterTags) {
        queryParams.append('tags', filterTags);
      }

      const headers = {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenant || 'default',
        'Authorization': `Bearer ${token}`,
      };

      const response = await fetch(`${CUSTOMERS_QUIZ_ENDPOINT}?${queryParams}`, { headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.status}`);
      }

      const data = await response.json();

      // Assuming your API returns results in data.results and count in data.count
      // Adjust based on your actual API response structure
      const customers = data.results || data;
      const count = data.count || customers.length;

      setRowCount(count);
      setCustomerSummary(customers);
      setFilteredCustomers(customers);

      // If your API doesn't return all attempts for each customer,
      // you might need to fetch them separately
      fetchAllQuizAttempts();
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Failed to load customers');
      showSnackbar('Error loading customer data', 'error');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Fetch all quiz attempts for detailed view
  const fetchAllQuizAttempts = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem('tenant');

      const headers = {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenant || 'default',
        'Authorization': `Bearer ${token}`,
      };

      // Fetch all attempts with a larger limit, or implement pagination if needed
      const response = await fetch(`${QUIZ_ATTEMPTS_ENDPOINT}?limit=100`, { headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch quiz attempts: ${response.status}`);
      }

      const data = await response.json();
      const attempts = data.results || data;
      // Transform the data to match your frontend structure
      const transformedAttempts = transformQuizAttempts(attempts);
      setAllResults(transformedAttempts);
    } catch (error) {
      console.error('Error fetching quiz attempts:', error);
      showSnackbar('Error loading quiz attempts', 'error');
    }
  };

  // Transform API quiz attempt data to frontend format
  const transformQuizAttempts = (attempts) => {
    return attempts.map(attempt => ({
      id: attempt.id,
      customerId: attempt.customer,
      customerName: attempt.customer_name || attempt.customer?.name || 'Unknown',
      email: attempt.customer_email || attempt.customer?.email || '',
      phone: attempt.customer?.phone || '',
      quizTitle: attempt.quiz_title || attempt.quiz?.title || 'Unknown Quiz',
      score: attempt.score || 0,
      totalQuestions: attempt.total_questions || 5,
      percentage: attempt.percentage || 0,
      date: attempt.completed_at || attempt.started_at || new Date().toISOString(),
      duration: attempt.duration_formatted || '0m 0s',
      passed: attempt.passed || false,
      answers: transformAnswers(attempt.answers || []),
    }));
  };

  // Transform API answer data to frontend format
  const transformAnswers = (answers) => {
    return answers.map(answer => ({
      q: answer.question_text || 'Question',
      customerAnswer: answer.customer_answer_text || answer.customer_answer || '',
      correctAnswer: answer.correct_answer_text || '',
      correct: answer.is_correct || false,
      questionNumber: answer.question_number || 0,
    }));
  };

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboardStats(),
        fetchCustomers(),
      ]);
      setLoading(false);
    };

    initializeData();
  }, []);

  // Refresh data when filters or pagination changes
  useEffect(() => {
    fetchCustomers();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    searchTerm,
    filterScore,
    filterCity,
    filterStatus,
    filterDateFrom,
    filterDateTo,
    filterTags,
  ]);

  const viewCustomerDetails = (customer) => {
    // Find all attempts for this customer
    const customerAttempts = allResults.filter(attempt =>
      attempt.customerId === customer.id
    );

    setSelectedCustomer({
      ...customer,
      attempts: customerAttempts,
    });
    setCurrentView('details');
  };

  const backToSummary = () => {
    setCurrentView('summary');
    setSelectedCustomer(null);
    setExpandedAttempts(new Set());
  };

  const toggleAttempt = (attemptId) => {
    const newExpanded = new Set(expandedAttempts);
    if (newExpanded.has(attemptId)) {
      newExpanded.delete(attemptId);
    } else {
      newExpanded.add(attemptId);
    }
    setExpandedAttempts(newExpanded);
  };

  // Export Summary — backend streams full unpaginated customer summary CSV
  const exportSummaryToCSV = async () => {
    try {
      showSnackbar('Preparing export…', 'info');
      const token = await getValidToken();
      const tenant = localStorage.getItem('tenant');

      const response = await fetch(QUIZ_EXPORT_SUMMARY_ENDPOINT, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenant || 'default',
        },
      });

      if (!response.ok) throw new Error(`Export failed: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `otm-quiz-export-summary-${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      showSnackbar('Summary exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showSnackbar('Export failed', 'error');
    }
  };

  // Export All — backend streams the full CSV, no pagination limits
  const exportAllToCSV = async () => {
    try {
      showSnackbar('Preparing export…', 'info');
      const token = await getValidToken();
      const tenant = localStorage.getItem('tenant');

      const response = await fetch(QUIZ_EXPORT_ALL_ENDPOINT, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenant || 'default',
        },
      });

      if (!response.ok) throw new Error(`Export failed: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `otm-quiz-export-all-${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      showSnackbar('All data exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showSnackbar('Export failed', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Export dropdown handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportSummary = () => {
    handleExportMenuClose();
    exportSummaryToCSV();
  };

  const handleExportAll = () => {
    handleExportMenuClose();
    exportAllToCSV();
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setController(prev => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const value = Number(event.target.value);
    setController((prev) => ({
      ...prev,
      rowsPerPage: value,
      page: 0,
      serverSidePage: 0,
    }));
  };

  const mainTableColumns = [
    {
      id: "phone",
      label: "WhatsApp Phone",
      render: (customer) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PhoneIcon fontSize="small" color="success" />
          <Typography variant="body2">{customer.phone || "No phone"}</Typography>
        </Box>
      ),
    },
    {
      // Audience Name is capped at AUDIENCE_NAME_MAX_WIDTH px so this column
      // doesn't blow out the table when a long name comes through. At the
      // 0.875rem (14px) body2 font size MUI uses here, that width fits
      // roughly 20-22 characters of average-width text (Roboto averages
      // ~7.5-8px per character at 14px) before it's cut off with "…". The
      // full, untruncated name always shows on hover via the Tooltip.
      id: "audience_name",
      label: "Audience Name",
      render: (customer) => {
        const fullName = customer.audience_name || '';
        return (
          <Tooltip title={fullName} arrow disableHoverListener={!fullName}>
            <Typography
              fontWeight="medium"
              variant="body2"
              noWrap
              sx={{ maxWidth: AUDIENCE_NAME_MAX_WIDTH, cursor: fullName ? 'default' : undefined }}
            >
              {fullName || '-'}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "name",
      label: "First Name",
      render: (customer) => (
        <Typography fontWeight="medium">{customer.name || customer.customerName}</Typography>
      ),
    },
    {
      id: "last_name",
      label: "Last Name",
      render: (customer) => (
        <Typography fontWeight="medium">{customer.last_name || "-"}</Typography>
      ),
    },
    {
      id: "city",
      label: "City",
      render: (customer) => (
        <Typography fontWeight="medium">{customer.city || "No City"}</Typography>
      ),
    },
    {
      id: "last_quiz_date",
      label: "Last Quiz Date",
      render: (customer) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarIcon fontSize="small" color="action" />
          <Box>
            {/* Date bumped to body2 (from body2, unchanged) and time bumped
                from `caption` (~0.75rem) to a readable 0.8125rem/medium
                weight so recent activity is easier to scan at a glance. */}
            <Typography variant="body2">
              {customer.last_quiz_date
                ? formatInTenantTz(customer.last_quiz_date, "DD-MM-YYYY")
                : "No attempts"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
              color="text.secondary"
            >
              {customer.last_quiz_date ? formatWithTzLabel(customer.last_quiz_date, "hh:mm A") : ""}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "latest_score",
      label: "Score",
      render: (customer) => (
        <Typography variant="body2" fontWeight="medium">
          {customer.latest_score ?? 0}
        </Typography>
      ),
    },
    {
      id: "total_attempts",
      label: "Total Attempts",
      render: (customer) => (
        <Chip label={customer.total_attempts || 0} color="primary" size="small" />
      ),
    },
    {
      id: "tags",
      label: "Tags",
      render: (customer) => (
        <Typography variant="body2">{customer.tags || "-"}</Typography>
      ),
    },
    {
      id: "chemist_type",
      label: "Type",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.type || "-"}</Typography>
      ),
    },
    {
      id: "chemist_sub_region",
      label: "Sub Region",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.sub_region || "-"}</Typography>
      ),
    },
    {
      id: "chemist_abm_hq",
      label: "ABM HQ",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.abm_hq || "-"}</Typography>
      ),
    },
    {
      id: "chemist_abm_name",
      label: "ABM Name",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.abm_name || "-"}</Typography>
      ),
    },
    {
      id: "chemist_mn_hq",
      label: "MN HQ",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.mn_hq || "-"}</Typography>
      ),
    },
    {
      id: "chemist_employee_name",
      label: "Employee Name",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.employee_name || "-"}</Typography>
      ),
    },
    {
      id: "chemist_ecubix_code",
      label: "Ecubix Code",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.ecubix_code || "-"}</Typography>
      ),
    },
    {
      id: "chemist_customer_type",
      label: "Customer Type",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.customer_type || "-"}</Typography>
      ),
    },
    {
      id: "chemist_current_rank",
      label: "Current Rank",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.current_rank || "-"}</Typography>
      ),
    },
    {
      id: "chemist_city",
      label: "Chemist City",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.city || "-"}</Typography>
      ),
    },
    {
      id: "chemist_state",
      label: "State",
      render: (customer) => (
        <Typography variant="body2">{customer.chemist_data?.state || "-"}</Typography>
      ),
    },
    {
      id: "__actions__",
      label: "Actions",
      render: (customer) => (
        <IconButton
          color="primary"
          size="small"
          onClick={(e) => { e.stopPropagation(); viewCustomerDetails(customer); }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];
  const attempts = selectedCustomer?.attempts ?? []

  const tableColumns = [
    {
      id: "__index__",
      label: "#",
      render: (attempt, index) => <strong>{index + 1}</strong>,
    },
    {
      id: "score",
      label: "Score",
      render: (attempt) => (
        <Typography variant="body2" fontWeight="medium">
          {attempt.score}
        </Typography>
      ),
    },
    {
      id: "date",
      label: "Date & Time",
      render: (attempt) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarIcon fontSize="small" color="action" />
          <Box>
            <Typography variant="body2">
              {formatInTenantTz(attempt.date, "DD-MM-YYYY")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
              color="text.secondary"
            >
              {formatWithTzLabel(attempt.date, "hh:mm A")}
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  // ── Expanded panel ─────────────────────────────────────────────────────────

  const renderExpanded = (attempt) => (
    <Box sx={{ bgcolor: "#f9f9f9", p: 3 }}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        Question Details
      </Typography>

      {!attempt.answers || attempt.answers.length === 0 ? (
        <Typography color="text.secondary" align="center" py={2}>
          No answer details available
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {attempt.answers.map((q, qIndex) => (
            <Paper
              key={qIndex}
              elevation={0}
              sx={{
                p: 2,
                border: 2,
                borderColor: q.correct ? "success.light" : "error.light",
                bgcolor: q.correct ? "success.lighter" : "error.lighter",
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* Question number badge */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: q.correct ? "success.main" : "error.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  {qIndex + 1}
                </Box>

                {/* Question content */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {q.q}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: q.correct ? "success.dark" : "error.dark", mb: 0.5 }}
                  >
                    <strong>Customer Answer:</strong> {q.customerAnswer}
                  </Typography>

                  {!q.correct && (
                    <Typography variant="body2" color="success.dark">
                      <strong>Correct Answer:</strong> {q.correctAnswer}
                    </Typography>
                  )}

                  <Box sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      icon={q.correct ? <CheckCircleIcon /> : <CancelIcon />}
                      label={q.correct ? "Correct" : "Incorrect"}
                      color={q.correct ? "success" : "error"}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );

  const menuItems = [
    { label: "All Scores", value: "all" },
    { label: "Score: 5", value: "5" },
    { label: "Score: 4", value: "4" },
    { label: "Score: 3", value: "3" },
    { label: "Score: 2", value: "2" },
    { label: "Score: 1", value: "1" },
    { label: "Score: 0", value: "0" },
  ]

  const statusMenuItems = [
    { label: "All Statuses", value: "all" },
    { label: "Passed", value: "passed" },
    { label: "Failed", value: "failed" },
  ];

  // Details View
  if (currentView === 'details' && selectedCustomer) {
    return (
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
        <Container maxWidth="lg">
          <Paper sx={{ p: 3, mb: 3 }}>
            <BackButton
              onClick={backToSummary}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 2 }}>
              <Box>
                <Typography gutterBottom sx={{ ...typography.h2 }}>
                  {selectedCustomer.name} {selectedCustomer.last_name || ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon fontSize="small" color="success" />
                  <Typography sx={{ ...typography.h2 }} color="text.secondary">{selectedCustomer.phone || 'No phone'}</Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary" sx={{ ...typography.h2 }}>
                  Total Attempts
                </Typography>
                <Typography variant="h3" color="primary" fontWeight="bold" sx={{ ...typography.statLarge }}>
                  {selectedCustomer.total_attempts || selectedCustomer.attempts?.length || 0}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography gutterBottom sx={{ ...typography.h1, mb: 1 }}>
              All Quiz Attempts
            </Typography>
            {selectedCustomer.attempts?.length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>
                No quiz attempts found for this customer
              </Typography>
            ) : (
              <AppTable
                columns={tableColumns}
                rows={[...attempts].sort((a, b) => new Date(b.date) - new Date(a.date))}
                rowKey="id"
                emptyMessage="No attempts found"
                headCellSx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}
                stickyHeader={false}
                paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
                isRowExpandable={(attempt) => attempt.answers?.length > 0}
                renderExpanded={renderExpanded}
              />
            )}
          </Paper>
        </Container>
      </Box>
    );
  }

  // Summary View
  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Box sx={{ px: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Statistics Cards — standardized, single design language */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              {STAT_CARDS_CONFIG.map((cfg) => (
                <Grid item xs={12} sm={6} md={3} key={cfg.key}>
                  <StatCard
                    label={cfg.label}
                    value={cfg.format(stats[cfg.key])}
                    Icon={cfg.icon}
                    accent={cfg.accent}
                    loading={loadingStats}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Search, Filter, Export */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TableSearchInput
                    searchQuery={searchTerm}
                    onSearchChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by WhatsApp Phone, First Name, Last Name, or City"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <AppDropdown
                      startIcon={<FilterListIcon />}
                      selectedValue={filterScore}
                      onMenuSelect={(value) => setFilterScore(value)}
                      menuItems={menuItems}
                    >
                      {menuItems.find((i) => i.value === filterScore)?.label ?? "All Scores"}
                    </AppDropdown>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    fullWidth
                    variant={showAdvancedFilters ? 'contained' : 'outlined'}
                    onClick={() => setShowAdvancedFilters((prev) => !prev)}
                    sx={{ height: '100%' }}
                  >
                    <Badge badgeContent={activeAdvancedFilterCount} color="error" sx={{ mr: activeAdvancedFilterCount ? 1.5 : 0 }}>
                      <FilterListIcon fontSize="small" />
                    </Badge>
                    Advanced
                  </Button>
                </Grid>
                <Grid item xs={6} md={2}>
                  <ButtonOne
                    fullWidth
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={handleExportClick}
                    sx={{ height: '100%' }}
                    disabled={filteredCustomers.length === 0}
                  >
                    Export
                  </ButtonOne>
                  <Menu
                    anchorEl={exportAnchorEl}
                    open={exportMenuOpen}
                    onClose={handleExportMenuClose}
                  >
                    <MenuItem onClick={handleExportSummary}>
                      Export Summary
                    </MenuItem>
                    <MenuItem onClick={handleExportAll}>
                      Export All
                    </MenuItem>
                  </Menu>
                </Grid>
              </Grid>

              {/* Advanced filters panel */}
              <Collapse in={showAdvancedFilters} timeout="auto" unmountOnExit>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      placeholder="e.g. Mumbai"
                      value={filterCity}
                      onChange={(e) => setFilterCity(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="status-filter-label">Pass / Fail</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        label="Pass / Fail"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        {statusMenuItems.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2.4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="From"
                      InputLabelProps={{ shrink: true }}
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2.4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="To"
                      InputLabelProps={{ shrink: true }}
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Audience Tags"
                      placeholder="e.g. VIP, Follow-up"
                      value={filterTags}
                      onChange={(e) => setFilterTags(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalOfferIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      startIcon={<RestartAltIcon />}
                      onClick={clearAdvancedFilters}
                      disabled={activeAdvancedFilterCount === 0}
                    >
                      Clear Advanced Filters
                    </Button>
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>

            {/* Customer Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              {loadingCustomers ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <AppTable
                    columns={mainTableColumns}
                    rows={filteredCustomers}
                    rowKey="id"
                    emptyMessage="No customers found"
                    count={rowCount}
                    page={controller.page}
                    rowsPerPage={controller.rowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>
              )}
            </Paper>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredCustomers.length} of {customerSummary.length} customers
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuizDashboardDynamic;